<template>
    <div class="flex flex-col gap-3 rounded-2xl border border-border bg-surface-raised px-5 py-4 md:flex-row md:items-center md:gap-4">
        <div class="flex-1 min-w-0">
            <p class="body-small-semibold text-ink">{{ t("journeys.managerBanner.question") }}</p>
            <p class="body-small text-ink-muted">{{ t("journeys.managerBanner.description") }}</p>
        </div>
        <div class="flex items-center justify-end gap-4 md:shrink-0">
            <BaseRadioGroup
                v-model="selectedValue"
                :options="managerOptions"
                orientation="horizontal"
                :aria-label="t('journeys.managerBanner.question')"
                :disabled="isSubmitting" />
            <BaseButton theme="secondary" size="sm" class="shrink-0" :disabled="!selectedValue || isSubmitting" @click="handleConfirm">
                {{ isSubmitting ? t("common.saving") : t("common.confirm") }}
            </BaseButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseRadioGroup from "~vue/components/form/BaseRadioGroup.vue";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { JourneysAmplitudeEventNames, logJourneysUserInteraction } from "./utils";

const emit = defineEmits<{ confirmed: [isManager: boolean] }>();

const { t } = useI18n();

const selectedValue = ref<"yes" | "no" | null>(null);
// Locale catalogue can finish loading after mount, so labels must stay reactive to the active locale.
const managerOptions = computed(() => [
    { value: "yes", label: t("common.yes") },
    { value: "no", label: t("common.no") },
]);
const isSubmitting = ref(false);

onMounted(() => {
    logJourneysUserInteraction(JourneysAmplitudeEventNames.MANAGER_BANNER_SHOWN);
});

const { execute: saveManagerStatus } = useFetch("/api/accounts/user", { immediate: false })
    .put(computed(() => ({ is_manager: selectedValue.value === "yes" })))
    .json();

async function handleConfirm() {
    if (!selectedValue.value || isSubmitting.value) {
        return;
    }
    isSubmitting.value = true;
    const isManager = selectedValue.value === "yes";
    await saveManagerStatus();
    isSubmitting.value = false;
    logJourneysUserInteraction(JourneysAmplitudeEventNames.MANAGER_BANNER_SUBMITTED, { is_manager: isManager });
    emit("confirmed", isManager);
}
</script>
